/**
 * Customer account scope for the portal.
 *
 * A portal login may be linked to several customer accounts (a hotel group is
 * one CRM parent plus one customer row per property). Every query in the
 * portal scopes to an EXPLICIT list of customer ids taken from those links —
 * never to "whatever RLS returns", because staff viewing the portal bypass
 * RLS and would otherwise see every customer.
 *
 * Two modes:
 *   • portfolio — selection = 'all': scopeIds is every active link. Only
 *     offered when the user has more than one account, and the default then.
 *   • single    — selection = a customer id: scopeIds is that one id and
 *     nothing from other accounts may render.
 *
 * Staff in view-as mode load the VIEWED user's links (staff RLS allows
 * reading them); the impersonation is recorded once per view-as session via
 * record_staff_view_as(). Staff without a target get no accounts — the
 * layout redirects them to the portal-user picker.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { useViewAs, VIEW_AS_RECORDED_KEY } from './ViewAsContext';

const STORAGE_PREFIX = 'pazportal:account_selection:';
export const ALL_ACCOUNTS = 'all';

export interface AccountSummary {
  customer_user_id: string;
  customer_id: string;
  name: string;
  account_number: string;
  role: string;
  is_primary_contact: boolean;
}

export type AccountSelection = typeof ALL_ACCOUNTS | string;

interface AccountState {
  /** Links are still being fetched. */
  loading: boolean;
  /** Links have settled (or no fetch was needed); scopeIds is trustworthy. */
  ready: boolean;
  error: Error | null;
  accounts: AccountSummary[];
  selection: AccountSelection;
  /** True when viewing every linked account at once. */
  isPortfolio: boolean;
  /** The single selected account; null in portfolio mode. */
  activeAccount: AccountSummary | null;
  /** Customer ids every query must scope to. Empty until ready. */
  scopeIds: string[];
  setSelection: (selection: AccountSelection) => void;
  /** Per-account role of the active account (null in portfolio mode). */
  activeRole: string | null;
  /**
   * Identifies whose links these are (the viewed users.id in view-as mode,
   * otherwise the auth user id). Query keys include it so caches never
   * cross view-as targets.
   */
  ownerKey: string;
}

const AccountContext = createContext<AccountState | undefined>(undefined);

function readSelection(ownerKey: string): AccountSelection | null {
  if (typeof window === 'undefined' || !ownerKey) return null;
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + ownerKey);
  } catch {
    return null;
  }
}

function writeSelection(ownerKey: string, selection: AccountSelection): void {
  if (typeof window === 'undefined' || !ownerKey) return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + ownerKey, selection);
  } catch {
    /* per-viewer convenience only */
  }
}

function defaultSelection(accounts: AccountSummary[]): AccountSelection {
  if (accounts.length > 1) return ALL_ACCOUNTS;
  return accounts[0]?.customer_id ?? ALL_ACCOUNTS;
}

export function AccountProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { isStaff, viewAsUserId, setAuditRecord } = useViewAs();

  const ownerKey = (isStaff ? viewAsUserId : user?.id) ?? '';
  // Staff only get links once they have chosen a portal user.
  const fetchEnabled = !!user && (isStaff ? !!viewAsUserId : true);

  const {
    data: accounts,
    isLoading,
    isSuccess,
    isError,
    error,
  } = useQuery({
    queryKey: ['customer-accounts', ownerKey],
    enabled: fetchEnabled,
    staleTime: 0,
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: 'always',
    refetchInterval: 30_000,
    queryFn: async (): Promise<AccountSummary[]> => {
      let query = supabase
        .from('customer_users')
        .select(
          `id, customer_id, role, is_primary_contact,
           customer:customers!customer_users_customer_id_fkey(name, account_number, deleted_at)`,
        )
        .eq('status', 'active');
      // Staff read the viewed user's links explicitly; a customer's own
      // links are already the only rows RLS returns.
      if (isStaff && viewAsUserId) query = query.eq('user_id', viewAsUserId);
      const { data, error: queryError } = await query;
      if (queryError) throw queryError;
      return (data ?? [])
        .map((row) => {
          const c = row.customer as
            | { name: string; account_number: string; deleted_at: string | null }
            | null;
          return {
            customer_user_id: row.id,
            customer_id: row.customer_id,
            name: c?.name ?? 'Unnamed account',
            account_number: c?.account_number ?? '',
            role: row.role,
            is_primary_contact: row.is_primary_contact,
            deleted: !!c?.deleted_at,
          };
        })
        .filter((a) => !a.deleted)
        .map(({ deleted: _deleted, ...a }) => a)
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  });

  // A failed revalidation must not keep using a formerly valid grant set.
  const accountList = useMemo(() => isError ? [] : accounts ?? [], [accounts, isError]);
  const ready = fetchEnabled ? isSuccess || isError : true;

  const grantKey = JSON.stringify(accountList.map((account) => [account.customer_id, account.role]).sort());
  const previousGrants = useRef<{ owner: string; key: string } | null>(null);
  useEffect(() => {
    if (!ready) return;
    const previous = previousGrants.current;
    if (previous && (previous.owner !== ownerKey || previous.key !== grantKey)) {
      // Discard responses and in-flight queries from permissions that changed.
      // Keep the queries that define the newly resolved account/view-as state.
      void queryClient.resetQueries({ predicate: (query) =>
        !['customer-accounts', 'view-as-user', 'customer-invitations'].includes(String(query.queryKey[0])),
      });
    }
    previousGrants.current = { owner: ownerKey, key: grantKey };
  }, [grantKey, ownerKey, queryClient, ready]);

  const [selection, setSelectionState] = useState<AccountSelection>(ALL_ACCOUNTS);

  // Restore the persisted selection for this owner, then reconcile it against
  // the actual link set so a stale id never yields an empty portal.
  useEffect(() => {
    if (!ready) return;
    const persisted = readSelection(ownerKey);
    const valid =
      persisted === ALL_ACCOUNTS
        ? accountList.length > 1
        : !!persisted && accountList.some((a) => a.customer_id === persisted);
    const next = valid ? (persisted as AccountSelection) : defaultSelection(accountList);
    setSelectionState(next);
    if (!valid) writeSelection(ownerKey, next);
  }, [accountList, ownerKey, ready]);

  const setSelection = useCallback(
    (next: AccountSelection) => {
      const valid =
        next === ALL_ACCOUNTS
          ? accountList.length > 1
          : accountList.some((a) => a.customer_id === next);
      const resolved = valid ? next : defaultSelection(accountList);
      setSelectionState(resolved);
      writeSelection(ownerKey, resolved);
    },
    [accountList, ownerKey],
  );

  const isPortfolio = selection === ALL_ACCOUNTS && accountList.length > 1;
  const activeAccount = useMemo(() => {
    if (isPortfolio) return null;
    return accountList.find((a) => a.customer_id === selection) ?? accountList[0] ?? null;
  }, [accountList, isPortfolio, selection]);

  const scopeIds = useMemo(() => {
    if (!ready) return [];
    if (isPortfolio) return accountList.map((a) => a.customer_id);
    return activeAccount ? [activeAccount.customer_id] : [];
  }, [accountList, activeAccount, isPortfolio, ready]);

  // Record the impersonation once per view-as session (sessionStorage marker).
  const recordingRef = useRef<string | null>(null);
  useEffect(() => {
    if (!isStaff || !viewAsUserId || !ready) return;
    let already: string | null = null;
    try {
      already = window.sessionStorage.getItem(VIEW_AS_RECORDED_KEY);
    } catch {
      already = null;
    }
    if (already === viewAsUserId) {
      setAuditRecord({ status: 'recorded' });
      return;
    }
    if (recordingRef.current === viewAsUserId) return;
    recordingRef.current = viewAsUserId;
    const primary =
      accountList.find((a) => a.is_primary_contact)?.customer_id ?? accountList[0]?.customer_id;
    void supabase
      .rpc('record_staff_view_as', {
        p_target_user_id: viewAsUserId,
        ...(primary ? { p_customer_id: primary } : {}),
      })
      .then(({ error: rpcError }) => {
        if (rpcError) {
          recordingRef.current = null;
          setAuditRecord({ status: 'failed', message: rpcError.message });
          return;
        }
        try {
          window.sessionStorage.setItem(VIEW_AS_RECORDED_KEY, viewAsUserId);
        } catch {
          /* marker is best-effort; the audit row exists */
        }
        setAuditRecord({ status: 'recorded' });
      });
  }, [accountList, isStaff, ready, setAuditRecord, viewAsUserId]);

  const value = useMemo<AccountState>(
    () => ({
      loading: fetchEnabled && isLoading,
      ready,
      error: isError ? (error as Error) : null,
      accounts: accountList,
      selection,
      isPortfolio,
      activeAccount,
      scopeIds,
      setSelection,
      activeRole: activeAccount?.role ?? null,
      ownerKey,
    }),
    [
      accountList,
      activeAccount,
      error,
      fetchEnabled,
      isError,
      isLoading,
      isPortfolio,
      ownerKey,
      ready,
      scopeIds,
      selection,
      setSelection,
    ],
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount(): AccountState {
  const ctx = useContext(AccountContext);
  if (!ctx) throw new Error('useAccount must be used within an AccountProvider');
  return ctx;
}

/** The single active account's id, or null in portfolio mode / before ready. */
export function useActiveCustomerId(): string | null {
  return useAccount().activeAccount?.customer_id ?? null;
}

export interface CustomerScope {
  /** Customer ids to scope queries to (`.in('customer_id', ids)`). */
  ids: string[];
  isPortfolio: boolean;
  /** False until the link set has settled — hooks should stay disabled. */
  ready: boolean;
  /** Stable key fragment for react-query: owner + sorted ids. */
  key: string;
  ownerKey: string;
}

/** Everything a hook needs to scope a customer-facing query. */
export function useCustomerScope(): CustomerScope {
  const { scopeIds, isPortfolio, ready, ownerKey } = useAccount();
  return useMemo(
    () => ({
      ids: scopeIds,
      isPortfolio,
      ready,
      key: `${ownerKey}|${[...scopeIds].sort().join(',')}`,
      ownerKey,
    }),
    [isPortfolio, ownerKey, ready, scopeIds],
  );
}
